import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "styled-components";

import theme from "./theme";

import Loader from "./components/ui/Loader";
import Home from "./pages/Home";
import { AppContext } from "./contexts/AppContext";
import queryString from "query-string";
import { apiUrl } from "./lib/config";
import { isDonationSubdomain } from "./lib/utils";
import { usePortalHostDecision } from "./hooks/usePortalHostDecision";
import { useExtensionDeeplinkHandoff } from "./hooks/useExtensionDeeplinkHandoff";
import { PORTAL_HOST, UNRESOLVED_HOST } from "./lib/portalHost";

const DataApiApp = lazy(() => import("./pages/dataApi/DataApiApp"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Blogs = lazy(() => import("./pages/Blogs"));
const Blog = lazy(() => import("./pages/Blog"));
const Guides = lazy(() => import("./pages/Guides"));
const Guide = lazy(() => import("./pages/Guide"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const CampaignProfessionals = lazy(
  () => import("./pages/CampaignProfessionals")
);
const CampaignProfessional = lazy(() => import("./pages/CampaignProfessional"));
const DeclaredCandidates = lazy(() => import("./pages/DeclaredCandidates"));
const DeclaredCandidate = lazy(() => import("./pages/DeclaredCandidate"));
const ListYourCampaign = lazy(() => import("./pages/ListYourCampaign"));
const ApplyAsAProfessional = lazy(() => import("./pages/ApplyAsAProfessional"));
const RecommendColleague = lazy(() => import("./pages/RecommendColleague"));
const AgencyOnboarding = lazy(() => import("./pages/AgencyOnboarding"));
const Pollies2026Giveaway = lazy(() => import("./pages/Pollies2026Giveaway"));
const Bbcon2026Giveaway = lazy(() => import("./pages/Bbcon2026Giveaway"));
const CampaignOnboarding = lazy(() => import("./pages/CampaignOnboarding"));
const EbookLandingPage = lazy(() => import("./pages/EbookLeadMagnets"));
const OptinsBrownFor5thWard = lazy(
  () => import("./pages/OptinsBrownFor5thWard")
);
const OptinsWashingtoniansForABrighterFuture = lazy(
  () => import("./pages/OptinsWashingtoniansForABrighterFuture")
);
const OptinsNextGenSD = lazy(() => import("./pages/OptinsNextGenSD"));
const OptinsSarahGalperinLLC = lazy(
  () => import("./pages/OptinsSarahGalperinLLC")
);
const OptinsSammyWyattForSenate = lazy(
  () => import("./pages/OptinsSammyWyattForSenate")
);
const OptinsReardonSullivan = lazy(
  () => import("./pages/OptinsReardonSullivan")
);
const OptinsChristineBishForCongress = lazy(
  () => import("./pages/OptinsChristineBishForCongress")
);
const OptinsDillonFillionForIowaHouse = lazy(
  () => import("./pages/OptinsDillonFillionForIowaHouse")
);
const OptinsJaredCraigForCongress = lazy(
  () => import("./pages/OptinsJaredCraigForCongress")
);
const OptinsBrentCaldwellForCongress = lazy(
  () => import("./pages/OptinsBrentCaldwellForCongress")
);
const OptinsFrankColligeForFloridaCFO = lazy(
  () => import("./pages/OptinsFrankColligeForFloridaCFO")
);
const OptinsKiddadaGreyForFulton = lazy(
  () => import("./pages/OptinsKiddadaGreyForFulton")
);
const OptinsReachThePeople = lazy(() => import("./pages/OptinsReachThePeople"));
const LandingPage20Pct = lazy(() => import("./pages/LandingPage20Pct"));
const LandingPage2Pct = lazy(() => import("./pages/LandingPage2Pct"));
const LandingPage40Pct = lazy(() => import("./pages/LandingPage40Pct"));
const EbookThankYouPage = lazy(() => import("./pages/EbookThankYou"));
const SubmissionThankYou = lazy(() => import("./pages/SubmissionThankYou"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const UserGuide = lazy(() => import("./pages/UserGuide"));
const ScheduleDemo = lazy(() => import("./pages/ScheduleDemo"));
const Video = lazy(() => import("./pages/Video"));
const DonationPage = lazy(() => import("./pages/DonationPage"));
const Proposal1903 = lazy(() => import("./pages/Proposal1903"));
const AAPCSampleCourse = lazy(() => import("./pages/AAPCSampleCourse"));
const ProposalAAPC = lazy(() => import("./pages/ProposalAAPC"));
const ProposalCAGOP = lazy(() => import("./pages/ProposalCAGOP"));
const ProposalBaycrest = lazy(() => import("./pages/ProposalBaycrest"));
const ProposalMoody = lazy(() => import("./pages/ProposalMoody"));
const ProposalSinger = lazy(() => import("./pages/ProposalSinger"));
const PoliticalCampaigns = lazy(() => import("./pages/PoliticalCampaigns"));
const ProposalJHA = lazy(() => import("./pages/ProposalJHA"));
const ProposalJHFNJ = lazy(() => import("./pages/ProposalJHFNJ"));
const ProposalSevenAcres = lazy(() => import("./pages/ProposalSevenAcres"));
const ProposalHOSPAC = lazy(() => import("./pages/ProposalHOSPAC"));
const ProposalPalmetto = lazy(() => import("./pages/ProposalPalmetto"));
const ProposalKlineGalland = lazy(() => import("./pages/ProposalKlineGalland"));
const ProposalSholom = lazy(() => import("./pages/ProposalSholom"));
const ProposalMcGruff = lazy(() => import("./pages/ProposalMcGruff"));
const ProposalNHL = lazy(() => import("./pages/ProposalNHL"));
const ProposalRumbleUp = lazy(() => import("./pages/ProposalRumbleUp"));
const Report = lazy(() => import("./pages/Report"));
const ImplementationJHA = lazy(() => import("./pages/ImplementationJHA"));
const ProposalGoodFood = lazy(() => import("./pages/ProposalGoodFood"));
const OnboardingPolitical = lazy(() => import("./pages/OnboardingPolitical"));
const ProposalChildrenBelieve = lazy(
  () => import("./pages/ProposalChildrenBelieve")
);
const Nonprofits = lazy(() => import("./pages/Nonprofits"));
const NationBuilder = lazy(() => import("./pages/NationBuilder"));
const Blackbaud = lazy(() => import("./pages/Blackbaud"));
const BlackbaudPricing = lazy(() => import("./pages/BlackbaudPricing"));
const OptinsScottSingerForCongress = lazy(
  () => import("./pages/OptinsScottSingerForCongress")
);
const OptinsScheelForCongress = lazy(
  () => import("./pages/OptinsScheelForCongress")
);
const OptinsMontgomeryCountyGOP = lazy(
  () => import("./pages/OptinsMontgomeryCountyGOP")
);
const NewHomepage = lazy(() => import("./pages/NewHomepage"));
const NewHomepage2 = lazy(() => import("./pages/NewHomepage2"));

// PDF Redirect Components
const PricingSheetRedirect = () => {
  window.location.href = "/ReachThePeoplePricing.pdf";
  return null;
};

const DeckRedirect = () => {
  window.location.href = "/ReachThePeopleForCampaigns.pdf";
  return null;
};

const NonprofitDeckRedirect = () => {
  window.location.href = "/ReachThePeopleForNonprofits.pdf";
  return null;
};

const OverviewRedirect = () => {
  window.location.href = "/reach-overview.pdf";
  return null;
};

const App = () => {
  const [extensionId, setExtensionId] = useState(undefined);
  const [resetToken, setResetToken] = useState(undefined);
  const [confirmationCode, setConfirmationCode] = useState(undefined);
  const [isUserNotCreateFirstCampaign, setIsUserNotCreateFirstCampaign] =
    useState(undefined);
  const [isReadyToLaunchCampaign, setIsReadyToLaunchCampaign] =
    useState(undefined);
  const [isWarnHighBounceRateCampaign, setIsWarnHighBounceRateCampaign] =
    useState(undefined);
  const [campaignId, setCampaignId] = useState(undefined);

  const portalDecision = usePortalHostDecision();

  useEffect(() => {
    const urlParams = queryString.parse(window.location.search, {
      parseBooleans: true,
    });

    if (urlParams.linkClickId) {
      localStorage.setItem("linkClickId", urlParams.linkClickId);
    }

    if (urlParams.rt) {
      setResetToken(urlParams.rt);
    }
    if (urlParams.cc) {
      setConfirmationCode(urlParams.cc);
    }

    if (urlParams.isUserNotCreateFirstCampaign) {
      setIsUserNotCreateFirstCampaign(urlParams.isUserNotCreateFirstCampaign);
    }
    if (urlParams.isReadyToLaunchCampaign) {
      setIsReadyToLaunchCampaign(urlParams.isReadyToLaunchCampaign);
    }
    if (urlParams.isWarnHighBounceRateCampaign) {
      setIsWarnHighBounceRateCampaign(urlParams.isWarnHighBounceRateCampaign);
    }
    if (urlParams.campaignId) {
      setCampaignId(urlParams.campaignId);
    }
    if (urlParams.r) {
      const redirectUrl = `${apiUrl}/r/${urlParams.r}`;
      window.location.href = redirectUrl;
      return;
    }

    const reachthepeopleExtensionId =
      window.reachthepeopleExtensionId || urlParams.extensionId;
    if (reachthepeopleExtensionId) {
      setExtensionId(reachthepeopleExtensionId);
    }
  }, []);

  // OUT-949: run the extension deeplink / auth handoff once here (route-independent) so it works on
  // the live homepage (`/` -> NewHomepage2), not only on legacy Home (`/old-hp`). AppLayout reads
  // isExtensionInstalled from AppContext to show the "extension not installed" popup.
  const isExtensionInstalled = useExtensionDeeplinkHandoff({
    extensionId,
    resetToken,
    confirmationCode,
    isUserNotCreateFirstCampaign,
    isReadyToLaunchCampaign,
    isWarnHighBounceRateCampaign,
    campaignId,
  });

  // White-labeled Data API reseller portal — fully isolated from the marketing site, sharing this
  // SPA build. The canonical host (dataapi.reachthepeople.ai, configurable via VITE_DATAAPI_HOSTS)
  // resolves instantly; reseller white-label domains are detected at runtime via the branding
  // endpoint, so an unknown host briefly renders a loader while it resolves.
  if (portalDecision === UNRESOLVED_HOST) {
    return (
      <Loader
        styles={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
    );
  }

  if (portalDecision === PORTAL_HOST) {
    return (
      <Suspense
        fallback={
          <Loader
            styles={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        }
      >
        <DataApiApp />
      </Suspense>
    );
  }

  return (
    <AppContext.Provider
      value={{
        extensionId,
        resetToken,
        confirmationCode,
        isUserNotCreateFirstCampaign,
        isReadyToLaunchCampaign,
        isWarnHighBounceRateCampaign,
        campaignId,
        isExtensionInstalled,
      }}
    >
      <ThemeProvider theme={theme}>
        <HelmetProvider>
          <Router>
            <Suspense
              fallback={
                <Loader
                  styles={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              }
            >
              {isDonationSubdomain() ? (
                <Routes>
                  <Route path="/give/:campaignTag" element={<DonationPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              ) : (
                <Routes>
                  <Route path="/" element={<NewHomepage2 />} />
                  <Route path="/old-hp" element={<Home />} />
                  <Route path="/blogs" element={<Blogs />} />
                  <Route path="/blog/:link" element={<Blog />} />
                  <Route path="/guides" element={<Guides />} />
                  <Route path="/guide/:link" element={<Guide />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms-conditions" element={<TermsOfUse />} />
                  <Route path="/data-deletion" element={<DataDeletion />} />
                  <Route path="/cookies" element={<CookiesPolicy />} />
                  <Route
                    path="/campaign-professionals"
                    element={<CampaignProfessionals />}
                  />
                  <Route
                    path="/campaign-professional/:slug"
                    element={<CampaignProfessional />}
                  />
                  <Route
                    path="/declared-candidates"
                    element={<DeclaredCandidates />}
                  />
                  <Route
                    path="/declared-candidates/:slug"
                    element={<DeclaredCandidate />}
                  />
                  <Route
                    path="/list-your-campaign"
                    element={<ListYourCampaign />}
                  />
                  <Route
                    path="/apply-as-a-professional"
                    element={<ApplyAsAProfessional />}
                  />
                  <Route
                    path="/professionals"
                    element={<RecommendColleague />}
                  />
                  <Route
                    path="/agency-onboarding"
                    element={<AgencyOnboarding />}
                  />
                  <Route
                    path="/pollies2026giveaway"
                    element={<Pollies2026Giveaway />}
                  />
                  <Route
                    path="/bbcon2026giveaway"
                    element={<Bbcon2026Giveaway />}
                  />
                  <Route
                    path="/campaign-onboarding"
                    element={<CampaignOnboarding />}
                  />
                  <Route
                    path="/optins/brown_for_5th_ward"
                    element={<OptinsBrownFor5thWard />}
                  />
                  <Route
                    path="/optins/washingtonians_for_a_brighter_future"
                    element={<OptinsWashingtoniansForABrighterFuture />}
                  />
                  <Route
                    path="/optins/nextgensd"
                    element={<OptinsNextGenSD />}
                  />
                  <Route
                    path="/optins/sarah_galperin_llc"
                    element={<OptinsSarahGalperinLLC />}
                  />
                  <Route
                    path="/optins/sammy_wyatt_for_senate"
                    element={<OptinsSammyWyattForSenate />}
                  />
                  <Route
                    path="/optins/reardon_sullivan"
                    element={<OptinsReardonSullivan />}
                  />
                  <Route
                    path="/optins/christine_bish_for_congress"
                    element={<OptinsChristineBishForCongress />}
                  />
                  <Route
                    path="/optins/dillon_fillion_for_iowa_house"
                    element={<OptinsDillonFillionForIowaHouse />}
                  />
                  <Route
                    path="/optins/jared_craig_for_congress"
                    element={<OptinsJaredCraigForCongress />}
                  />
                  <Route
                    path="/optins/brent_caldwell_for_congress"
                    element={<OptinsBrentCaldwellForCongress />}
                  />
                  <Route
                    path="/optins/frank_collige_for_florida_cfo"
                    element={<OptinsFrankColligeForFloridaCFO />}
                  />
                  <Route
                    path="/optins/scott_singer_for_congress"
                    element={<OptinsScottSingerForCongress />}
                  />
                  <Route
                    path="/optins/scheel_for_congress"
                    element={<OptinsScheelForCongress />}
                  />
                  <Route
                    path="/optins/montgomery_county_gop"
                    element={<OptinsMontgomeryCountyGOP />}
                  />
                  <Route
                    path="/optins/kiddada_grey_for_fulton"
                    element={<OptinsKiddadaGreyForFulton />}
                  />
                  <Route
                    path="/optins/reachthepeople"
                    element={<OptinsReachThePeople />}
                  />
                  <Route path="/lp/20pct" element={<LandingPage20Pct />} />
                  <Route path="/lp/2pct" element={<LandingPage2Pct />} />
                  <Route path="/lp/40pct" element={<LandingPage40Pct />} />
                  <Route path="/ebook" element={<EbookLandingPage />} />
                  <Route
                    path="/ebook-thank-you"
                    element={<EbookThankYouPage />}
                  />
                  <Route
                    path="/submission-thank-you"
                    element={<SubmissionThankYou />}
                  />
                  <Route path="/resources/user-guide" element={<UserGuide />} />
                  <Route path="/agencies" element={<ROICalculator />} />
                  <Route path="/schedule-demo" element={<ScheduleDemo />} />
                  <Route path="/video" element={<Video />} />
                  <Route
                    path="/pricing-sheet"
                    element={<PricingSheetRedirect />}
                  />
                  <Route path="/deck" element={<DeckRedirect />} />
                  <Route path="/campaigns-deck" element={<DeckRedirect />} />
                  <Route
                    path="/nonprofit-deck"
                    element={<NonprofitDeckRedirect />}
                  />
                  <Route path="/overview" element={<OverviewRedirect />} />
                  <Route path="/nonprofits" element={<Nonprofits />} />
                  <Route path="/blackbaud" element={<Blackbaud />} />
                  <Route
                    path="/blackbaud/pricing"
                    element={<BlackbaudPricing />}
                  />
                  <Route path="/give/:campaignTag" element={<DonationPage />} />
                  <Route path="/proposals/1903" element={<Proposal1903 />} />
                  <Route path="/proposals/aapc" element={<ProposalAAPC />} />
                  <Route
                    path="/aapc-ai-certification-sample-course"
                    element={<AAPCSampleCourse />}
                  />
                  <Route
                    path="/proposals/baycrest"
                    element={<ProposalBaycrest />}
                  />
                  <Route path="/proposals/moody" element={<ProposalMoody />} />
                  <Route
                    path="/proposals/singer"
                    element={<ProposalSinger />}
                  />
                  <Route
                    path="/politicalcampaigns"
                    element={<PoliticalCampaigns />}
                  />
                  <Route path="/nationbuilder" element={<NationBuilder />} />
                  <Route path="/proposals/jha" element={<ProposalJHA />} />
                  <Route path="/proposals/jhfnj" element={<ProposalJHFNJ />} />
                  <Route path="/proposals/sevenacres" element={<ProposalSevenAcres />} />
                  <Route path="/proposals/hospac" element={<ProposalHOSPAC />} />
                  <Route path="/proposals/palmetto" element={<ProposalPalmetto />} />
                  <Route path="/proposals/klinegalland" element={<ProposalKlineGalland />} />
                  <Route path="/proposals/sholom" element={<ProposalSholom />} />
                  <Route path="/proposals/mcgruff" element={<ProposalMcGruff />} />
                  <Route path="/proposals/nhl" element={<ProposalNHL />} />
                  <Route path="/proposals/rumbleup" element={<ProposalRumbleUp />} />
                  <Route
                    path="/implementations/jha"
                    element={<ImplementationJHA />}
                  />
                  <Route path="/new_hp" element={<NewHomepage />} />
                  <Route
                    path="/proposals/goodfood"
                    element={<ProposalGoodFood />}
                  />
                  <Route
                    path="/onboarding/political"
                    element={<OnboardingPolitical />}
                  />
                  <Route
                    path="/proposals/childrenbelieve"
                    element={<ProposalChildrenBelieve />}
                  />
                  <Route path="/proposals/cagop" element={<ProposalCAGOP />} />
                  <Route
                    path="/reports/:companySlug/:campaignSlug"
                    element={<Report />}
                  />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              )}
            </Suspense>
          </Router>
        </HelmetProvider>
      </ThemeProvider>
    </AppContext.Provider>
  );
};

export default App;
